import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const contactInfo = [
  {
    icon: MapPin,
    title: 'China Office',
    content: '501, Haoyuetianjin Community\nHaigang District, Qinhuangdao City\nHebei Province, China',
  },
  {
    icon: MapPin,
    title: 'US Office',
    content: '750 Cliff Road\nPort Reading, NJ 07008\nUnited States',
  },
  {
    icon: Phone,
    title: 'Phone',
    content: '+1 (201) 234-4851',
    link: 'tel:+12012344851',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'sales@engineshippart.com',
    link: 'mailto:sales@engineshippart.com',
  },
  {
    icon: Clock,
    title: 'Business Hours',
    content: 'Mon - Fri: 8:00 AM - 6:00 PM\nSat: 9:00 AM - 2:00 PM',
  },
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const engineId = searchParams.get('engine');
  const [formType, setFormType] = useState(engineId ? 'quote' : 'contact');
  const [loading, setLoading] = useState(false);
  const [selectedEngine, setSelectedEngine] = useState(null);
  
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    engine_id: engineId || '',
    message: '',
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    if (engineId) {
      fetchEngine(engineId);
    }
  }, [engineId]);

  const fetchEngine = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/engines/${id}`);
      setSelectedEngine(response.data);
      setQuoteForm(prev => ({ 
        ...prev, 
        engine_id: id,
        message: `I'm interested in the ${response.data.title}. Please provide pricing and availability information.`
      }));
    } catch (error) {
      console.error('Failed to fetch engine:', error);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/quotes`, quoteForm);
      toast.success('Quote request submitted successfully!');
      setQuoteForm({ name: '', email: '', phone: '', company: '', engine_id: '', message: '' });
      setSelectedEngine(null);
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/contact`, contactForm);
      toast.success('Message sent successfully!');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Header */}
      <div className="bg-slate-900 text-white py-16">
        <div className="container-custom">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">Get In Touch</h1>
          <p className="text-xl text-slate-400 max-w-2xl">
            Have questions about our marine engines? Need a custom quote? 
            Our team of specialists is ready to help.
          </p>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Contact Information</h2>
            {contactInfo.map((info, idx) => {
              const Icon = info.icon;
              return (
                <div key={idx} className="flex gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                    {info.link ? (
                      <a href={info.link} className="text-slate-600 hover:text-orange-500 transition-colors whitespace-pre-line">
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-slate-600 whitespace-pre-line">{info.content}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Cummins Engine Image */}
            <div className="mt-8 aspect-video bg-slate-200 rounded-sm overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/levb3zan_Cummins%20KTA50-M%201000HP%20Diesel%20Marine%20Engine.jpg" 
                alt="Cummins Marine Engine" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-sm border border-slate-200 p-8">
              {/* Form Type Toggle */}
              <div className="flex gap-4 mb-8">
                <Button
                  variant={formType === 'quote' ? 'default' : 'outline'}
                  onClick={() => setFormType('quote')}
                  className={formType === 'quote' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  data-testid="quote-tab"
                >
                  Request Quote
                </Button>
                <Button
                  variant={formType === 'contact' ? 'default' : 'outline'}
                  onClick={() => setFormType('contact')}
                  className={formType === 'contact' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  data-testid="contact-tab"
                >
                  General Inquiry
                </Button>
              </div>

              {/* Quote Form */}
              {formType === 'quote' && (
                <form onSubmit={handleQuoteSubmit} className="space-y-6" data-testid="quote-form">
                  {selectedEngine && (
                    <div className="bg-slate-50 rounded-sm p-4 flex items-center gap-4 mb-6">
                      <img 
                        src={selectedEngine.images?.[0] || 'https://images.pexels.com/photos/35591861/pexels-photo-35591861.jpeg'} 
                        alt={selectedEngine.title}
                        className="w-20 h-14 object-cover rounded-sm"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{selectedEngine.title}</p>
                        <p className="text-sm text-slate-600">{selectedEngine.brand} • {selectedEngine.horsepower} HP</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="q-name">Full Name *</Label>
                      <Input
                        id="q-name"
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="quote-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="q-email">Email *</Label>
                      <Input
                        id="q-email"
                        type="email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="quote-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="q-phone">Phone</Label>
                      <Input
                        id="q-phone"
                        type="tel"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        className="mt-2"
                        data-testid="quote-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="q-company">Company</Label>
                      <Input
                        id="q-company"
                        value={quoteForm.company}
                        onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })}
                        className="mt-2"
                        data-testid="quote-company"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="q-message">Message *</Label>
                    <Textarea
                      id="q-message"
                      rows={5}
                      value={quoteForm.message}
                      onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                      required
                      className="mt-2"
                      placeholder="Please describe your requirements..."
                      data-testid="quote-message"
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full md:w-auto" disabled={loading} data-testid="quote-submit">
                    {loading ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Quote Request
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Contact Form */}
              {formType === 'contact' && (
                <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="c-name">Full Name *</Label>
                      <Input
                        id="c-name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="c-email">Email *</Label>
                      <Input
                        id="c-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="c-phone">Phone</Label>
                      <Input
                        id="c-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="mt-2"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="c-subject">Subject *</Label>
                      <Input
                        id="c-subject"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="c-message">Message *</Label>
                    <Textarea
                      id="c-message"
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      className="mt-2"
                      placeholder="How can we help you?"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full md:w-auto" disabled={loading} data-testid="contact-submit">
                    {loading ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
